import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import Modal from "../../components/common/modal";
import Button from "../../components/common/button";
import { useViewIndustryQuery } from "../../hooks/industry/industryList";
import { useViewCategory } from "../../hooks/categorys/category.showall";
import { getClientValidationSchema } from "../../validation/client";
import "react-country-state-city/dist/react-country-state-city.css";
import { CountrySelect } from "react-country-state-city";
import { resolveImageUrl } from "../../utils/resolveImageUrl";

const emptyValues = {
  logo: null,
  name: "",
  email: "",
  phone: "",
  country: "",
  industry: "",
  category: "",
};

const resolveRelationId = (value) => {
  if (!value) return "";
  if (Array.isArray(value)) {
    const first = value[0];
    return first?._id || first || "";
  }
  return value?._id || value || "";
};

const ClientModal = ({
  isOpen,
  onClose,
  client,
  mode,
  onSave,
  isLoading,
}) => {
  const { data: industriesData, isLoading: industriesLoading } =
    useViewIndustryQuery();
  const { data: categoriesData, isLoading: categoriesLoading } =
    useViewCategory();

  const industriesList = industriesData?.data || industriesData || [];
  const categoriesList =
    categoriesData?.data ||
    categoriesData?.categories ||
    categoriesData ||
    [];

  const viewMode = mode === "view";
  const isEditing = mode === "edit";
  const isCreate = mode === "create";
  const prevIsOpenRef = useRef(false);
  const logoInputRef = useRef(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [countrySelectKey, setCountrySelectKey] = useState(0);
  const hasExistingLogo = !!(client?.logo);

  const formik = useFormik({
    enableReinitialize: isEditing && !!client,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues:
      isCreate || !client
        ? emptyValues
        : {
            logo: null,
            name: client.name || "",
            email: client.email || "",
            phone: client.phone || "",
            country: client.country || "",
            industry: resolveRelationId(client.industry),
            category: resolveRelationId(client.category),
          },
    validationSchema: getClientValidationSchema(isEditing, hasExistingLogo),
    onSubmit: (values) => {
      const formDataObj = new FormData();

      if (values.logo && typeof values.logo !== "string") {
        formDataObj.append("logo", values.logo);
      }

      formDataObj.append("name", values.name || "");
      formDataObj.append("email", values.email || "");
      formDataObj.append("phone", values.phone || "");
      formDataObj.append("country", values.country || "");

      if (values.industry) {
        formDataObj.append("industry", values.industry);
      }
      if (values.category) {
        formDataObj.append("category", values.category);
      }

      onSave && onSave(formDataObj);
    },
  });

  useEffect(() => {
    if (!isOpen) {
      prevIsOpenRef.current = false;
      return;
    }

    const justOpened = !prevIsOpenRef.current;
    prevIsOpenRef.current = true;

    if (!justOpened) return;

    if (isCreate || !client) {
      formik.resetForm({ values: emptyValues });
      setLogoPreview("");
      setCountrySelectKey((k) => k + 1);
      return;
    }

    setCountrySelectKey((k) => k + 1);

    formik.resetForm({
      values: {
        logo: null,
        name: client.name || "",
        email: client.email || "",
        phone: client.phone || "",
        country: client.country || "",
        industry: resolveRelationId(client.industry),
        category: resolveRelationId(client.category),
      },
    });

    if (client.logo) {
      setLogoPreview(resolveImageUrl(client.logo));
    } else {
      setLogoPreview("");
    }
  }, [isOpen, isCreate, client]);

  useEffect(() => {
    return () => {
      if (logoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleFieldChange = (e) => {
    formik.handleChange(e);
  };

  const handleCountryChange = (country) => {
    formik.setFieldValue("country", country.name);
    formik.setFieldTouched("country", true, false);
    formik.validateField("country");
    setCountrySelectKey((k) => k + 1);
    requestAnimationFrame(() => {
      document.body.dispatchEvent(
        new MouseEvent("click", { bubbles: true }),
      );
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] || null;

    formik.setFieldValue("logo", file);
    formik.setFieldTouched("logo", true, false);
    formik.validateField("logo");

    if (logoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }

    if (file) {
      setLogoPreview(URL.createObjectURL(file));
    } else if (hasExistingLogo && client?.logo) {
      setLogoPreview(resolveImageUrl(client.logo));
    } else {
      setLogoPreview("");
    }
  };

  const logoFileName =
    formik.values.logo instanceof File
      ? formik.values.logo.name
      : hasExistingLogo && logoPreview
        ? String(client.logo?.url || client.logo || "")
            .split("/")
            .pop() || "Current logo"
        : "No file chosen";

  const handleSave = () => {
    formik.setTouched({
      logo: true,
      name: true,
      email: true,
      phone: true,
      country: true,
      industry: true,
      category: true,
    });
    formik.handleSubmit();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          mode === "delete"
            ? "Delete Client"
            : viewMode
              ? "View Client"
              : client
                ? "Edit Client"
                : "Add Client"
        }
      >
        {mode === "delete" ? (
          <>
            <div style={{ padding: "10px 2px" }}>
              <p style={{ margin: 0, color: "#333" }}>
                Are you sure you want to delete{" "}
                <strong>{client?.name || "this client"}</strong>?
              </p>
              <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>
                This action will remove it from the database and the table.
              </p>
            </div>

            <div className="modal-footer">
              <Button onClick={onClose} className="CancelButton">
                Cancel
              </Button>
              <Button
                onClick={() => onSave?.(null)}
                disabled={isLoading}
                className="CommonButton"
              >
                {isLoading ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="form-container">
              <div className="form-group">
                <label htmlFor="logo">Logo</label>
                <div className="client-logo-upload">
                  {logoPreview && (
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="client-logo-preview"
                    />
                  )}
                  {!viewMode && (
                    <div className="client-logo-file-wrap">
                      <input
                        ref={logoInputRef}
                        type="file"
                        id="logo"
                        className="client-logo-input-hidden"
                        accept=".png,.jpg,.jpeg"
                        onChange={handleLogoChange}
                      />
                      <label
                        htmlFor="logo"
                        className="client-logo-choose-btn"
                      >
                        Choose File
                      </label>
                      <span className="client-logo-filename">
                        {logoFileName}
                      </span>
                    </div>
                  )}
                  {viewMode && logoPreview && (
                    <span className="client-logo-filename">{logoFileName}</span>
                  )}
                </div>
                {formik.touched.logo && formik.errors.logo && (
                  <p className="error-text">{formik.errors.logo}</p>
                )}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="clientName">Client Name</label>
                  <input
                    id="clientName"
                    name="name"
                    placeholder="e.g., Acme Corporation"
                    value={formik.values.name}
                    onChange={handleFieldChange}
                    onBlur={formik.handleBlur}
                    readOnly={viewMode}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <p className="error-text">{formik.errors.name}</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="clientEmail">Email</label>
                  <input
                    id="clientEmail"
                    name="email"
                    placeholder="contact@company.com"
                    value={formik.values.email}
                    onChange={handleFieldChange}
                    onBlur={formik.handleBlur}
                    readOnly={viewMode}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <p className="error-text">{formik.errors.email}</p>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="clientPhone">Phone (Optional)</label>
                  <input
                    id="clientPhone"
                    name="phone"
                    placeholder="+1 (555) 000 - 0000"
                    value={formik.values.phone}
                    onChange={handleFieldChange}
                    onBlur={formik.handleBlur}
                    readOnly={viewMode}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <p className="error-text">{formik.errors.phone}</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="clientCountry">Country</label>
                  {viewMode ? (
                    <input
                      id="clientCountry"
                      name="country"
                      value={formik.values.country}
                      readOnly
                    />
                  ) : (
                    <CountrySelect
                      key={`client-country-select-${countrySelectKey}`}
                      placeHolder="Select Country"
                      defaultValue={formik.values.country}
                      onChange={handleCountryChange}
                      onBlur={() => formik.setFieldTouched("country", true)}
                    />
                  )}
                  {formik.touched.country && formik.errors.country && (
                    <p className="error-text">{formik.errors.country}</p>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="clientIndustry">Industry</label>
                  <select
                    id="clientIndustry"
                    name="industry"
                    className="form-field-select"
                    value={formik.values.industry}
                    onChange={handleFieldChange}
                    onBlur={formik.handleBlur}
                    disabled={viewMode || industriesLoading}
                  >
                    <option value="">Select Industry</option>
                    {industriesList.map((ind) => (
                      <option key={ind._id || ind.id} value={ind._id || ind.id}>
                        {ind.title}
                      </option>
                    ))}
                  </select>
                  {formik.touched.industry && formik.errors.industry && (
                    <p className="error-text">{formik.errors.industry}</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="clientCategory">Category</label>
                  <select
                    id="clientCategory"
                    name="category"
                    className="form-field-select"
                    value={formik.values.category}
                    onChange={handleFieldChange}
                    onBlur={formik.handleBlur}
                    disabled={viewMode || categoriesLoading}
                  >
                    <option value="">Select Category</option>
                    {categoriesList.map((cat) => (
                      <option key={cat._id || cat.id} value={cat._id || cat.id}>
                        {cat.title}
                      </option>
                    ))}
                  </select>
                  {formik.touched.category && formik.errors.category && (
                    <p className="error-text">{formik.errors.category}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <Button className="CancelButton" onClick={onClose}>
                {viewMode ? "Close" : "Cancel"}
              </Button>
              {!viewMode && (
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={isLoading}
                  className="CommonButton"
                >
                  {isLoading ? "Saving..." : "Save Client"}
                </Button>
              )}
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default ClientModal;
